import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { startOfWeek, addDays, format, startOfDay } from 'date-fns';
import {
  CalendarDays,
  Users,
  CheckCircle,
  AlertCircle,
  MapPin,
  List,
  LayoutGrid,
  XCircle,
} from 'lucide-react';
import { supabase } from '../../services/supabase';
import { useAuthStore } from '../../store/authStore';
import SedeFilter from '../../components/SedeFilter';
import TimeGrid, { type TimeGridEvent } from '../../components/TimeGrid';
import {
  videoBookingToEvent,
  isVideoAnalysisEvent,
  getVideoBookingId,
  type VideoAnalysisBookingRow,
} from '../../lib/videoAnalysisAgenda';
import { useAgendaHours } from '../../lib/useAgendaHours';
import {
  quienLleva,
  etiquetaQuienLleva,
  nombreDePila,
  esCanceladaPorElClub,
  bloqueDeRejilla,
  ETIQUETA_CANCELADA,
  COLORES_CANCELADA,
  type TrainerSessionRow,
} from '../../lib/quienLleva';
import { listaEnCastellano } from '../../lib/leerVarias';
import AgendaHoursControl from '../../components/AgendaHoursControl';
import { diaDe, hoy } from '../../lib/fechaLocal';

// Rango de fechas cargado por la agenda según su fecha+vista (C-2.1): vista día
// → ese día; vista semana → lunes a domingo de esa semana.
function agendaRange(date: Date, view: 'day' | 'week'): { from: string; to: string } {
  if (view === 'day') {
    const s = format(date, 'yyyy-MM-dd');
    return { from: s, to: s };
  }
  const mon = startOfWeek(date, { weekStartsOn: 1 });
  return { from: format(mon, 'yyyy-MM-dd'), to: format(addDays(mon, 6), 'yyyy-MM-dd') };
}

type AgendaFilter = 'today' | 'week' | 'upcoming' | 'past';

const AGENDA_FILTERS: AgendaFilter[] = ['today', 'week', 'upcoming', 'past'];
function isAgendaFilter(v: string | null): v is AgendaFilter {
  return v != null && (AGENDA_FILTERS as string[]).includes(v);
}

interface AgendaSession {
  id: string;
  date: string;
  time_start: string;
  time_end: string;
  group_name: string;
  group_level: string;
  group_sede: string | null;
  confirmed: number;
  pending: number;
  /**
   * La canceló el club. ⚠️ ESTA PANTALLA SALÍA ILESA POR ACCIDENTE: el badge
   * iba guardado con `confirmed > 0`, y una sesión cancelada tiene sus
   * inscripciones en `cancelled`, así que no decía «Listo»… pero pintaba un
   * `0` pelado, que tampoco es la verdad. Un cero dice «no se ha apuntado
   * nadie»; lo que pasó es que la sesión no existe.
   */
  cancelada: boolean;
  // mig 280: la agenda ya no es solo la del entrenador. `mia` distingue las
  // suyas de las del resto del club.
  mia: boolean;
  // mig 313: y ya se puede decir DE QUIÉN es. El nombre no se podía resolver
  // desde el cliente —un monitor no ve el perfil de otro monitor: las cinco
  // policies de SELECT de `profiles` no cubren monitor↔monitor—, así que lo
  // resuelve el RPC, que es SECURITY DEFINER.
  monitor_name: string | null;
  // No la lleva NADIE por ninguna de las tres vías. No es lo mismo que «no sé
  // su nombre»: es un hueco que alguien tiene que rellenar.
  sin_asignar: boolean;
}

const LEVEL_COLORS: Record<string, string> = {
  basico: '#4CAF50',
  medio: '#2196F3',
  avanzado: '#FF9800',
  tecnificacion: '#FFF200',
};
const LEVEL_LABELS: Record<string, string> = {
  basico: 'Básico',
  medio: 'Medio',
  avanzado: 'Avanzado',
  tecnificacion: 'Tecnif.',
};

// Color de acento del bloque de agenda según tipo/nivel (misma paleta que la lista).
function agendaColor(kind: string, level: string | null): string {
  if (kind === 'especial') return '#F59E0B';
  if (kind === 'individual') return '#9CA3AF';
  return LEVEL_COLORS[level ?? ''] ?? '#FFF200';
}

// Evento de agenda: TimeGridEvent + la sede en meta (para el filtro por sede).
interface AgendaEventMeta {
  sede: string | null;
  // mig 280: si la sesión es del propio entrenador o de otro del club.
  mia: boolean;
  // mig 313: el nombre completo del que la lleva (el bloque enseña el de pila).
  monitorName: string | null;
}

const FILTERS: { key: AgendaFilter; label: string }[] = [
  { key: 'today', label: 'Hoy' },
  { key: 'week', label: 'Esta semana' },
  { key: 'upcoming', label: 'Próximas' },
  { key: 'past', label: 'Pasadas' },
];

function getDateRange(filter: AgendaFilter) {
  const now = new Date();
  // El día de calendario LOCAL, que es lo que entiende una columna `date`.
  // `toISOString()` daría la fecha UTC: de 00:00 a 02:00, ayer. Ver §140.
  const today = diaDe(now);
  if (filter === 'today') return { from: today, to: today };
  if (filter === 'week') {
    const to = new Date(now);
    to.setDate(now.getDate() + 7);
    return { from: today, to: diaDe(to) };
  }
  if (filter === 'upcoming') {
    const to = new Date(now);
    to.setDate(now.getDate() + 60);
    return { from: today, to: diaDe(to) };
  }
  const from = new Date(now);
  from.setDate(now.getDate() - 60);
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  return { from: diaDe(from), to: diaDe(yesterday) };
}

// La agenda del entrenador: lo que mira para saber si hoy trabaja. Las lecturas
// encadenadas tiraban el error, y las salidas anticipadas dejaban
// `setSessions([])`, que la pantalla remata con «No hay sesiones en este
// período».
//
// mig 313: `los grupos del club` se cae de aquí porque la lista ya no lee
// `groups` — pregunta lo mismo que la rejilla.
const QUE = {
  sesiones: 'las sesiones',
  asistencias: 'las asistencias',
} as const;

export default function MonitorAgendaPage() {
  const { profile } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sessions, setSessions] = useState<AgendaSession[]>([]);
  // Permite aterrizar en una pestaña concreta vía ?filter= (p.ej. el dashboard
  // enlaza a ?filter=past para mostrar las sesiones pasadas). Default: semana.
  const initialFilter = searchParams.get('filter');
  const [filter, setFilter] = useState<AgendaFilter>(
    isAgendaFilter(initialFilter) ? initialFilter : 'week'
  );
  // S-4: filtro multi-selección por sede (nombres; vacío = todas). Filtra en
  // cliente sobre la sede ya cargada en cada sesión (group_sede), sin queries.
  const [filterSedes, setFilterSedes] = useState<string[]>([]);
  // mig 313: mías / todas. Arranca en TODAS a propósito — ver el comentario del
  // conmutador, más abajo.
  const [soloMias, setSoloMias] = useState(false);
  const [loading, setLoading] = useState(true);
  // C-2: alternancia lista / agenda (rejilla horaria). La lista se conserva.
  const [viewMode, setViewMode] = useState<'list' | 'agenda'>('list');
  const [agendaEvents, setAgendaEvents] = useState<TimeGridEvent[]>([]);
  const [agendaLoading, setAgendaLoading] = useState(false);
  const [bloquesFallidos, setBloquesFallidos] = useState<string[]>([]);
  // C-2.1: la agenda es la DUEÑA de su fecha+vista. Cambiarlas (desde el TimeGrid
  // o programáticamente) dispara la recarga del rango correspondiente.
  const [agendaDate, setAgendaDate] = useState<Date>(() => startOfDay(new Date()));
  const [agendaView, setAgendaView] = useState<'day' | 'week'>('week');
  // C-4: rango horario de la agenda, preferencia por usuario (default 8–24).
  const { hourStart, hourEnd, save: saveAgendaHours, saving: savingHours } = useAgendaHours();

  const load = useCallback(
    async (f: AgendaFilter) => {
      if (!profile) return;
      setLoading(true);

      // mig 313 · UNA SOLA FUENTE. Hasta aquí la lista leía `groups` y
      // `sessions` por su cuenta y se calculaba el entrenador efectivo en
      // cliente, con un COALESCE copiado del RPC que además se había quedado
      // corto: no miraba `special_classes.monitor_id`. Ahora pregunta
      // exactamente lo mismo que la rejilla, y el efectivo lo decide el
      // servidor por las tres vías.
      //
      // Dos cosas cambian de paso, y las dos se querían:
      //  · entran las sesiones de CLASE ESPECIAL. No tienen `group_id`, así
      //    que el `.in('group_id', …)` de antes las dejaba fuera de la lista
      //    aunque sí salieran en la rejilla.
      //  · el RPC no filtra por `groups.active`, y la query de antes sí.
      // Medido en producción antes de cambiar la fuente (hoy..±60 días): 88
      // sesiones, 0 colgando de un grupo inactivo —hay 3 grupos inactivos, sin
      // sesiones en el rango— y 0 de clase especial. Hoy la lista sale igual;
      // lo que cambia es que cuando las haya, saldrán.
      const { from, to } = getDateRange(f);
      const { data: filas, error: errSessions } = await supabase.rpc('get_trainer_sessions', {
        p_trainer_id: profile.id,
        p_from: from,
        p_to: to,
      });

      // ⚠️ Un fallo aquí vaciaba la agenda entera y la pantalla lo remataba
      //    afirmando que no hay sesiones en el período.
      if (errSessions) {
        setBloquesFallidos([QUE.sesiones]);
        setSessions([]);
        setLoading(false);
        return;
      }

      const sessionData = (filas as unknown as TrainerSessionRow[] | null) ?? [];
      if (!sessionData.length) {
        setBloquesFallidos([]);
        setSessions([]);
        setLoading(false);
        return;
      }

      // El RPC ordena SIEMPRE ascendente. «Pasadas» se leía del día más
      // reciente hacia atrás —que es como se mira lo que ya pasó—, y dentro de
      // cada día en orden de reloj. Sin esto la pestaña arrancaría hace dos
      // meses.
      const ordenadas =
        f === 'past'
          ? [...sessionData].sort((a, b) =>
              a.date === b.date
                ? a.time_start.localeCompare(b.time_start)
                : b.date.localeCompare(a.date)
            )
          : sessionData;

      // Los recuentos de asistencia siguen aparte: el RPC no los trae.
      const sessionIds = ordenadas.map((s) => s.id);
      const { data: enrollData, error: errEnroll } = await supabase
        .from('enrollments')
        .select('session_id, attendance')
        .in('session_id', sessionIds)
        .eq('status', 'confirmed');

      // Las sesiones sí se leyeron: se pintan. Lo que no se afirma es el
      // recuento de cada una («0 alumnos», «Listo»).
      setBloquesFallidos(errEnroll ? [QUE.asistencias] : []);

      type EnrollmentBrief = { session_id: string; attendance: boolean | null };
      const attendMap: Record<string, { confirmed: number; pending: number }> = {};
      for (const e of (enrollData ?? []) as EnrollmentBrief[]) {
        if (!attendMap[e.session_id]) attendMap[e.session_id] = { confirmed: 0, pending: 0 };
        attendMap[e.session_id].confirmed++;
        if (e.attendance === null) attendMap[e.session_id].pending++;
      }

      setSessions(
        ordenadas.map((s) => {
          const q = quienLleva(s, profile.id);
          return {
            id: s.id,
            date: s.date,
            time_start: s.time_start,
            time_end: s.time_end,
            // El título del RPC: el nombre del grupo, o el de la clase
            // especial cuando la sesión cuelga de una.
            group_name: s.title,
            group_level: s.level ?? 'basico',
            group_sede: s.sede,
            confirmed: attendMap[s.id]?.confirmed ?? 0,
            pending: attendMap[s.id]?.pending ?? 0,
            cancelada: esCanceladaPorElClub(s),
            mia: q.mia,
            monitor_name: q.nombre,
            sin_asignar: q.sinAsignar,
          };
        })
      );
      setLoading(false);
    },
    [profile]
  );

  useEffect(() => {
    load(filter);
  }, [load, filter]);

  // C-2/C-3: datos de la AGENDA (vista rejilla). Sesiones vía get_trainer_sessions
  // (grupos del entrenador + sesiones asignadas por override) y, en la misma carga
  // del rango (C-3), sus vídeo-análisis AGENDADOS (video_analysis_bookings con
  // scheduled_at, trainer_id = él). Se carga solo cuando la vista es 'agenda'.
  const loadAgenda = useCallback(
    async (date: Date, view: 'day' | 'week') => {
      if (!profile) return;
      setAgendaLoading(true);
      const { from, to } = agendaRange(date, view);
      const [sessionsRes, bookingsRes] = await Promise.all([
        supabase.rpc('get_trainer_sessions', {
          p_trainer_id: profile.id,
          p_from: from,
          p_to: to,
        }),
        supabase
          .from('video_analysis_bookings')
          .select('id, scheduled_at, status, trainer_id, user_id')
          .eq('trainer_id', profile.id)
          .eq('status', 'scheduled')
          .not('scheduled_at', 'is', null)
          .gte('scheduled_at', `${from}T00:00:00`)
          .lte('scheduled_at', `${to}T23:59:59.999`),
      ]);
      const sessionEvents: TimeGridEvent[] =
        sessionsRes.error || !sessionsRes.data
          ? []
          : (sessionsRes.data as unknown as TrainerSessionRow[]).map((s) => {
              // mig 313 · EL TÍTULO DICE QUIÉN. Hasta aquí las ajenas se
              // marcaban con un punto delante —`· Alevin A`— porque no se podía
              // saber de quién eran: un monitor no ve el perfil de otro
              // monitor. Ahora el RPC trae el nombre resuelto en servidor, y el
              // punto pasa a ser el separador de un nombre: `Alevin A · Alex`.
              //
              // Va el nombre DE PILA, no el completo: en un bloque de 60
              // minutos el título ya compite con el nombre del grupo y el
              // TimeGrid recorta por la derecha. El completo viaja en `meta`.
              const q = quienLleva(s, profile.id);
              const corto = q.sinAsignar ? 'Sin asignar' : (nombreDePila(q.nombre) ?? 'Entrenador');
              // La precedencia también en la REJILLA: no filtra por estado, así
              // que una cancelada se pintaba como un bloque cualquiera con el
              // color de su nivel. El rótulo y el rojo salen del gemelo, para
              // que el separador y el orden no puedan divergir con la app.
              const bloque = bloqueDeRejilla(s, {
                title: q.mia ? s.title : `${s.title} · ${corto}`,
                // Su color para las propias, gris para las del resto. Las que
                // no lleva nadie van también en gris —no son suyas— y lo que
                // avisa es el texto: el ámbar que usa la LISTA para ese caso
                // chocaría aquí con el nivel avanzado, que ya es naranja.
                color: q.mia ? agendaColor(s.kind, s.level) : '#6B7280',
              });
              return {
                id: s.id,
                title: bloque.title,
                start: new Date(`${s.date}T${s.time_start}`),
                end: new Date(`${s.date}T${s.time_end}`),
                color: bloque.color,
                meta: {
                  sede: s.sede,
                  mia: q.mia,
                  monitorName: q.nombre,
                } satisfies AgendaEventMeta,
              };
            });
      const videoEvents: TimeGridEvent[] =
        bookingsRes.error || !bookingsRes.data
          ? []
          : (bookingsRes.data as VideoAnalysisBookingRow[])
              .map(videoBookingToEvent)
              .filter((e): e is TimeGridEvent => e !== null);
      setAgendaEvents([...sessionEvents, ...videoEvents]);
      setAgendaLoading(false);
    },
    [profile]
  );

  useEffect(() => {
    if (viewMode === 'agenda') loadAgenda(agendaDate, agendaView);
  }, [viewMode, agendaDate, agendaView, loadAgenda]);

  // El filtro por sede va PRIMERO: es el que decide qué hay en pantalla, y los
  // recuentos de Mías/Todas tienen que contar eso y no el club entero.
  const sedeVisibles = filterSedes.length
    ? sessions.filter((s) => filterSedes.includes(s.group_sede ?? ''))
    : sessions;
  const visibleSessions = soloMias ? sedeVisibles.filter((s) => s.mia) : sedeVisibles;
  const byDate: Record<string, AgendaSession[]> = {};
  visibleSessions.forEach((s) => {
    if (!byDate[s.date]) byDate[s.date] = [];
    byDate[s.date].push(s);
  });
  const dates = Object.keys(byDate);

  // Agenda: eventos visibles (filtrados por sede en cliente). Los vídeo-análisis
  // (C-3) NO tienen sede en BD → el filtro por sede no les aplica: se conservan.
  const visibleAgendaEvents = useMemo(
    () =>
      filterSedes.length
        ? agendaEvents.filter(
            (e) =>
              isVideoAnalysisEvent(e) ||
              filterSedes.includes((e.meta as AgendaEventMeta | undefined)?.sede ?? '')
          )
        : agendaEvents,
    [agendaEvents, filterSedes]
  );

  // Mías, en la rejilla. Los vídeo-análisis son SIEMPRE del que mira
  // (`trainer_id = profile.id` en la consulta), así que con «Mías» se quedan:
  // esconderlos escondería trabajo suyo.
  const eventosVisibles = useMemo(
    () =>
      soloMias
        ? visibleAgendaEvents.filter(
            (e) => isVideoAnalysisEvent(e) || (e.meta as AgendaEventMeta | undefined)?.mia === true
          )
        : visibleAgendaEvents,
    [visibleAgendaEvents, soloMias]
  );

  // Recuentos del conmutador. Se cuentan sobre lo que habría SIN estrechar por
  // mías: es lo que hace que «Mías (0)» se lea como «aquí no llevas ninguna» y
  // no como «no hay sesiones».
  //
  // Cada vista cuenta LO SUYO porque cargan rangos distintos: la lista va por
  // pestañas (hoy, semana, próximas, pasadas) y la rejilla por su propia
  // semana. Contar una con los datos de la otra daría un número que no
  // corresponde a nada de lo que se está viendo.
  const sesionesRejilla = useMemo(
    () => visibleAgendaEvents.filter((e) => !isVideoAnalysisEvent(e)),
    [visibleAgendaEvents]
  );
  const nTodas = viewMode === 'agenda' ? sesionesRejilla.length : sedeVisibles.length;
  const nMias =
    viewMode === 'agenda'
      ? sesionesRejilla.filter((e) => (e.meta as AgendaEventMeta | undefined)?.mia === true).length
      : sedeVisibles.filter((s) => s.mia).length;
  const formatDayHeader = (dateStr: string) => {
    const d = new Date(`${dateStr}T12:00:00`);
    const todayStr = hoy();
    const prefix = dateStr === todayStr ? 'HOY · ' : '';
    return (
      prefix +
      d
        .toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })
        .toUpperCase()
    );
  };

  return (
    <div className="space-y-6">
      {bloquesFallidos.length > 0 && (
        <div
          className="card border-error/40 bg-error/5"
          data-testid="agenda-fallidos"
          role="status"
        >
          <p className="text-error text-sm">
            No se han podido cargar {listaEnCastellano([...bloquesFallidos])}. Una agenda vacía por
            esto no significa que no tengas sesiones.
          </p>
        </div>
      )}

      {/* Filter tabs — solo en modo LISTA. En agenda el navegador es el TimeGrid
          (día/semana con su propia fecha), así que las pestañas no aplican. */}
      {viewMode === 'list' && (
        <div className="flex items-center gap-2 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 rounded-full text-sm font-inter border transition-colors ${
                filter === f.key
                  ? 'bg-gold-primary text-bg-primary border-gold-primary font-semibold'
                  : 'bg-bg-surface text-text-secondary border-bg-border hover:border-gold-primary/40 hover:text-white'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {/* Filtro por sede (S-4) — se oculta solo si hay <2 sedes activas */}
      <SedeFilter selected={filterSedes} onChange={setFilterSedes} />

      {/* C-2: alternar lista / agenda (rejilla horaria). */}
      <div className="flex gap-1 bg-bg-surface border border-bg-border rounded-xl p-1 w-fit">
        <button
          onClick={() => setViewMode('list')}
          data-testid="monitor-agenda-view-list"
          className={`px-4 py-1.5 rounded-lg text-sm font-inter flex items-center gap-2 transition-colors ${
            viewMode === 'list'
              ? 'bg-gold-primary text-bg-primary font-semibold'
              : 'text-text-secondary hover:text-white'
          }`}
        >
          <List size={14} /> Lista
        </button>
        <button
          onClick={() => setViewMode('agenda')}
          data-testid="monitor-agenda-view-agenda"
          className={`px-4 py-1.5 rounded-lg text-sm font-inter flex items-center gap-2 transition-colors ${
            viewMode === 'agenda'
              ? 'bg-gold-primary text-bg-primary font-semibold'
              : 'text-text-secondary hover:text-white'
          }`}
        >
          <LayoutGrid size={14} /> Agenda
        </button>
      </div>

      {/* mig 313 · MÍAS / TODAS. Fila propia, separada de las pestañas de fecha:
          son dos ejes distintos y mezclarlos en la misma tira se lee como un
          solo grupo de opciones. Sale en las DOS vistas porque filtra las dos.

          Arranca en «Todas» a propósito: la mig 280 abrió el club entero
          porque los entrenadores rotan, y entrar viendo solo las tuyas
          escondería justo lo que se quiso abrir. El filtro está para quien
          quiera estrechar.

          Los recuentos van dentro del botón: sin ellos, «Mías» con la lista
          vacía se lee como «no hay sesiones». */}
      <div
        className="flex gap-1 bg-bg-surface border border-bg-border rounded-xl p-1 w-fit"
        role="group"
        aria-label="Sesiones mías o de todo el club"
      >
        <button
          onClick={() => setSoloMias(false)}
          data-testid="agenda-filtro-todas"
          aria-pressed={!soloMias}
          className={`px-4 py-1.5 rounded-lg text-sm font-inter transition-colors ${
            !soloMias
              ? 'bg-gold-primary text-bg-primary font-semibold'
              : 'text-text-secondary hover:text-white'
          }`}
        >
          Todas ({nTodas})
        </button>
        <button
          onClick={() => setSoloMias(true)}
          data-testid="agenda-filtro-mias"
          aria-pressed={soloMias}
          className={`px-4 py-1.5 rounded-lg text-sm font-inter transition-colors ${
            soloMias
              ? 'bg-gold-primary text-bg-primary font-semibold'
              : 'text-text-secondary hover:text-white'
          }`}
        >
          Mías ({nMias})
        </button>
      </div>

      {/* C-4: ajuste del rango horario (solo en agenda). Guardar refleja al instante. */}
      {viewMode === 'agenda' && (
        <AgendaHoursControl
          start={hourStart}
          end={hourEnd}
          onChange={saveAgendaHours}
          disabled={savingHours}
        />
      )}

      {viewMode === 'agenda' ? (
        agendaLoading ? (
          <div className="flex items-center justify-center h-48">
            <div className="text-gold-primary font-oswald animate-pulse">Cargando...</div>
          </div>
        ) : (
          <TimeGrid
            events={eventosVisibles}
            date={agendaDate}
            onDateChange={setAgendaDate}
            view={agendaView}
            onViewChange={setAgendaView}
            hourStart={hourStart}
            hourEnd={hourEnd}
            onEventClick={(e) =>
              navigate(
                isVideoAnalysisEvent(e)
                  ? `/monitor/dashboard?booking=${getVideoBookingId(e)}`
                  : `/monitor/sesion/${e.id}`
              )
            }
          />
        )
      ) : loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="text-gold-primary font-oswald animate-pulse">Cargando...</div>
        </div>
      ) : dates.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-48 gap-3 text-text-secondary">
          <CalendarDays size={40} className="opacity-30" />
          <p className="font-inter text-sm" data-testid="agenda-vacio">
            {/* Con «Mías» puesto y sesiones del club en pantalla, «no hay
                sesiones» contradice al recuento que se está leyendo justo
                encima («Todas (34)»). Son dos cosas distintas y se dicen
                distinto. */}
            {bloquesFallidos.length > 0
              ? 'No se ha podido cargar la agenda.'
              : soloMias && nTodas > 0
                ? 'No llevas ninguna sesión en este período'
                : 'No hay sesiones en este período'}
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {dates.map((date) => (
            <div key={date}>
              <p className="font-oswald text-xs text-text-secondary tracking-widest mb-3">
                {formatDayHeader(date)}
              </p>
              <div className="space-y-2">
                {byDate[date].map((s) => {
                  const color = LEVEL_COLORS[s.group_level] ?? '#FFF200';
                  return (
                    <button
                      key={s.id}
                      onClick={() => navigate(`/monitor/sesion/${s.id}`)}
                      className="card w-full text-left hover:border-gold-primary/40 transition-colors group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {/* Time */}
                          <div className="text-center min-w-[48px]">
                            <p className="font-oswald text-gold-primary text-lg leading-none">
                              {s.time_start.slice(0, 5)}
                            </p>
                            <p className="font-inter text-text-secondary text-xs">
                              –{s.time_end.slice(0, 5)}
                            </p>
                          </div>
                          {/* Info */}
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-oswald text-white text-base">{s.group_name}</p>
                              {/* mig 313 · EL CHIP DICE QUIÉN. Antes ponía
                                  «Del club» en las ajenas y nada en las
                                  propias: la tarjeta no decía de quién era
                                  ninguna. Ahora lleva siempre el nombre
                                  —también en las tuyas, para que la columna se
                                  lea igual de arriba abajo— y lo que distingue
                                  las tuyas es el amarillo. Y si no la lleva
                                  nadie, ámbar: es un hueco que alguien tiene
                                  que rellenar, no un dato neutro. */}
                              <span
                                data-testid={`agenda-quien-${s.id}`}
                                className={`font-inter text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 border ${
                                  s.mia
                                    ? 'text-gold-primary border-gold-primary/40'
                                    : s.sin_asignar
                                      ? 'text-[#FF9800] border-[#FF9800]/40'
                                      : 'text-text-secondary border-white/15'
                                }`}
                              >
                                {etiquetaQuienLleva({
                                  mia: s.mia,
                                  sinAsignar: s.sin_asignar,
                                  nombre: s.monitor_name,
                                })}
                              </span>
                            </div>
                            <span
                              className="inline-block text-xs font-inter font-semibold uppercase tracking-wide mt-1 px-2 py-0.5 rounded"
                              style={{
                                color,
                                backgroundColor: `${color}20`,
                                border: `1px solid ${color}50`,
                              }}
                            >
                              {LEVEL_LABELS[s.group_level] ?? s.group_level}
                            </span>
                            {s.group_sede && (
                              <p className="text-text-secondary text-xs font-inter mt-1 flex items-center gap-1">
                                <MapPin size={11} className="flex-shrink-0" /> {s.group_sede}
                              </p>
                            )}
                          </div>
                        </div>
                        {/* Right */}
                        <div className="flex items-center gap-3">
                          <span className="flex items-center gap-1 text-text-secondary text-xs font-inter">
                            <Users size={13} /> {s.confirmed}
                          </span>
                          {/* ⚠️ LA PRECEDENCIA, Y AQUÍ MANDA TAMBIÉN SOBRE LA
                              GUARDA DE `confirmed > 0`. Con esa guarda esta
                              pantalla no decía «Listo» sobre una cancelada,
                              pero se callaba: pintaba un `0` y nada más. Un
                              cero dice «no se ha apuntado nadie», y lo que
                              pasó es otra cosa. Copiado de la pantalla del
                              alumno: «la sesión cancelada por el club manda
                              sobre el estado del enrollment». */}
                          {s.cancelada ? (
                            <span
                              data-testid={`agenda-cancelada-${s.id}`}
                              className="flex items-center gap-1 text-xs font-inter rounded px-1.5"
                              style={{
                                color: COLORES_CANCELADA.text,
                                backgroundColor: COLORES_CANCELADA.bg,
                                border: `1px solid ${COLORES_CANCELADA.border}`,
                              }}
                            >
                              <XCircle size={13} /> {ETIQUETA_CANCELADA}
                            </span>
                          ) : (
                            s.confirmed > 0 &&
                            (s.pending > 0 ? (
                              <span
                                data-testid={`agenda-pend-${s.id}`}
                                className="flex items-center gap-1 text-[#FF9800] text-xs font-inter"
                              >
                                <AlertCircle size={13} /> {s.pending} pend.
                              </span>
                            ) : (
                              <span
                                data-testid={`agenda-listo-${s.id}`}
                                className="flex items-center gap-1 text-[#4CAF50] text-xs font-inter"
                              >
                                <CheckCircle size={13} /> Listo
                              </span>
                            ))
                          )}
                          <span className="text-text-secondary group-hover:text-gold-primary transition-colors">
                            →
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
